import { toDateStr, type PeriodRange } from './periodUtils';

/** バックエンド AnalysisController と同一のエラーコード */
export const FREE_ANALYSIS_PERIOD_OUT_OF_RANGE_CODE = 'FREE_ANALYSIS_PERIOD_OUT_OF_RANGE';

export class FreeAnalysisPeriodRejectedError extends Error {
    override readonly name = 'FreeAnalysisPeriodRejectedError';

    constructor() {
        super(FREE_ANALYSIS_PERIOD_OUT_OF_RANGE_CODE);
    }
}

function calendarYmdPartsInTimeZone(isoInstant: Date, timeZone: string): { y: number; m: number; d: number } {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(isoInstant);
    const y = Number(parts.find((p) => p.type === 'year')?.value);
    const m = Number(parts.find((p) => p.type === 'month')?.value);
    const d = Number(parts.find((p) => p.type === 'day')?.value);
    return { y, m, d };
}

function ymdString(y: number, m: number, d: number): string {
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/**
 * 無料プランで閲覧可能な日付範囲（先月1日〜翌月末、ユーザーTZの「今」基準）。
 * サーバー側 LocalDate.now(zone) + withDayOfMonth 相当。
 */
export function getFreeAnalysisWindowYmdBounds(timeZone?: string): { start: string; end: string } {
    const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { y, m } = calendarYmdPartsInTimeZone(new Date(), tz);

    let py = y;
    let pm = m - 1;
    if (pm < 1) {
        pm = 12;
        py -= 1;
    }
    const start = ymdString(py, pm, 1);

    let ny = y;
    let nm = m + 1;
    if (nm > 12) {
        nm = 1;
        ny += 1;
    }
    const lastDayNextMonth = new Date(ny, nm, 0).getDate();
    const end = ymdString(ny, nm, lastDayNextMonth);

    return { start, end };
}

/**
 * 表示期間が無料ウィンドウに完全に含まれるか。
 * ウィンドウはサーバー同様にユーザーTZの「今日」で計算。
 * 期間の日付は API の formatDateForServer と同じくローカル暦（getFullYear/getMonth/getDate）で比較する。
 * （Instant を別TZでフォーマットすると前月末などがずれて誤判定するため）
 */
export function isPeriodWithinFreeAnalysisWindow(period: PeriodRange, timeZone?: string): boolean {
    const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const { start: wStart, end: wEnd } = getFreeAnalysisWindowYmdBounds(tz);
    const ps = toDateStr(period.start);
    const pe = toDateStr(period.end);
    return ps >= wStart && pe <= wEnd;
}
